import type {
  ImageModelsRefreshResponse,
  SaveImageModel,
} from "@nlc/shared-types";

import { useState } from "react";

import { Button } from "@/components/button";
import { Label } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { toast } from "@/components/toast/toast-queue";

import { resolveAiCostsRange } from "../aiCostsSearch";
import {
  useImageModelCosts,
  useImageModels,
  useRefreshImageModels,
  useSaveAlphaSettings,
  useSaveImageModels,
} from "../hooks/useImageModels";
import { isSameModel, toSaveModel } from "./imageModelDraft";
import { ImageModelsTable } from "./ImageModelsTable";
import { type ImageModelCandidate, NewModelsSection } from "./NewModelsSection";

function toastError(title: string, error: Error) {
  toast.add(
    { description: error.message, title, type: "error" },
    { timeout: 8000 },
  );
}

function toSaveCandidate({
  description: _description,
  displayName: _displayName,
  ...model
}: ImageModelCandidate): SaveImageModel {
  return model;
}

export function ImageModelsTab() {
  const models = useImageModels();
  const costs = useImageModelCosts(
    resolveAiCostsRange({ period: "last-30-days" })!,
  );
  const saveModels = useSaveImageModels();
  const saveSettings = useSaveAlphaSettings();
  const refreshModels = useRefreshImageModels();

  const [drafts, setDrafts] = useState<Record<string, SaveImageModel>>({});
  const [added, setAdded] = useState<Record<string, SaveImageModel>>({});
  const [refresh, setRefresh] = useState<ImageModelsRefreshResponse | null>(
    null,
  );

  const persisted = models.data?.models ?? [];
  const pendingCount = Object.keys(drafts).length + Object.keys(added).length;

  const updateModel = (model: SaveImageModel) => {
    const original = persisted.find(({ key }) => key === model.key);

    setDrafts(({ [model.key]: _previous, ...rest }) =>
      original && isSameModel(toSaveModel(original), model)
        ? rest
        : { ...rest, [model.key]: model },
    );
  };

  const selectCandidate = (
    candidate: ImageModelCandidate,
    isSelected: boolean,
  ) =>
    setAdded(({ [candidate.key]: _previous, ...rest }) =>
      isSelected
        ? { ...rest, [candidate.key]: toSaveCandidate(candidate) }
        : rest,
    );

  const rows = persisted.map((model) => {
    const draft = drafts[model.key];

    return {
      costMicroUsd: (costs.data?.rows ?? [])
        .filter((row) => row.model === model.key)
        .reduce((sum, row) => sum + row.costMicroUsd, 0),
      isChanged: !!draft,
      model: draft ?? toSaveModel(model),
      refreshed: refresh?.inUse.find(({ key }) => key === model.key),
    };
  });

  const editModels = persisted.filter(({ alpha }) => alpha.edit);

  const save = () =>
    saveModels.mutate(
      { models: [...Object.values(drafts), ...Object.values(added)] },
      {
        onError: (error) => toastError("Could not save models", error),
        onSuccess: () => {
          setDrafts({});
          setAdded({});
          setRefresh(null);
        },
      },
    );

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-end gap-6 px-xl py-6">
        <Select
          className="w-72"
          isDisabled={!models.data || saveSettings.isPending}
          onSelectionChange={(key) =>
            saveSettings.mutate(
              { defaultEditModel: String(key) },
              {
                onError: (error) =>
                  toastError("Could not change the default edit model", error),
              },
            )
          }
          selectedKey={models.data?.settings.defaultEditModel ?? null}
        >
          <Label>Default edit model</Label>
          <SelectButton />
          <SelectPopover>
            <SelectListBox
              items={editModels.map(({ key, label }) => ({ key, label }))}
            >
              {(item) => (
                <SelectListItem
                  className="cursor-pointer"
                  id={item.key}
                  textValue={item.label}
                >
                  {item.label}
                </SelectListItem>
              )}
            </SelectListBox>
          </SelectPopover>
        </Select>

        <div className="ml-auto flex gap-3">
          <Button
            color="gray"
            isLoading={refreshModels.isPending}
            onPress={() =>
              refreshModels.mutate(undefined, {
                onError: (error) =>
                  toastError("Could not refresh from fal", error),
                onSuccess: setRefresh,
              })
            }
            variant="secondary"
          >
            Refresh from fal
          </Button>
          <Button
            isDisabled={pendingCount === 0}
            isLoading={saveModels.isPending}
            onPress={save}
          >
            Save changes
          </Button>
        </div>
      </div>

      {models.error && (
        <p className="px-xl pb-4 text-sm text-text-error" role="alert">
          Could not load image models: {models.error.message}
        </p>
      )}

      <ImageModelsTable
        isLoading={models.isLoading}
        onChange={updateModel}
        rows={rows}
      />

      {refresh && (
        <NewModelsSection
          candidates={refresh.candidates}
          onChange={(model) =>
            setAdded((current) => ({ ...current, [model.key]: model }))
          }
          onSelect={selectCandidate}
          selected={added}
        />
      )}
    </div>
  );
}
